import React from 'react';
import { Box, Button, CircularProgress, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import T, { bool, func } from 'prop-types';
import { FormElements, NotifyTypo } from 'web-components';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  getChangeNumberHandler,
  getDefaultFieldValue,
  getFormikError,
  getFormikHelperText,
  isLoading,
  validateBlankSpace
} from 'helpers/utils';
import {
  StyledContent,
  StyledContentGridColumn,
  StyledGridItem,
  StyledHandlers,
  StyledNewDialog
} from '../../elements';
import { IrisBatchTargetPLCSchema } from '../../../../../attrs/formValidation';
import { IrisBatchTargetPropTypes } from '../../../../../helpers/propTypes';
import { getSelectedIrisBatchTarget } from '../../../../../redux/machines_v2/selectors';
import { updateIrisBatchTarget } from '../../../../../redux/machines_v2/actions';
import { PlcType } from '../../../../../attrs/plcConfigValues';

const labelAmend = 'machines.machine_details.batch_iris.target';
const labelAmendItem = `${labelAmend}.plc_target_label`;

const IrisBatchRecordsEditTargetPLC = ({ open, machineId, handleClose, data, plcType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loadingState } = useSelector(getSelectedIrisBatchTarget);

  function isSaving() {
    return isLoading(loadingState.status);
  }

  const submitForm = (values, formikHelpers) => {
    dispatch(updateIrisBatchTarget(machineId, { plc_target: values }));
    handleClose(true);
    formikHelpers.resetForm();
  };

  const actionBar = (handleSubmit, errors, resetForm, dirty) => (
    <StyledHandlers>
      <Button
        color="secondary"
        onClick={() => {
          resetForm();
          handleClose();
        }}
      >
        {t('customers.form.cancel')}
      </Button>
      <Button
        variant="contained"
        data-testid="plc-modal-submit"
        style={{ padding: '1rem 2.5rem' }}
        disabled={!dirty || Object.keys(errors).length !== 0 || isSaving()}
        onClick={() => handleSubmit()}
        onMouseDown={event => {
          event.preventDefault();
        }}
      >
        {t(`default_actions.save`)}
        {isSaving() && <CircularProgress sx={{ marginLeft: 1, fontSize: '1.125rem' }} size={18} />}
      </Button>
    </StyledHandlers>
  );

  const handleChangeValue = handleChange => event => {
    const { name, value } = event.target;

    let newValue = validateBlankSpace(value);

    if (plcType.plc_type === PlcType.IRIS_V3_INSIDE_S7) {
      newValue = newValue.toUpperCase();
    }

    handleChange({ target: { name, value: newValue } });
  };

  return (
    <Formik
      validationSchema={IrisBatchTargetPLCSchema(plcType)}
      initialValues={{
        grinding_energy_address: getDefaultFieldValue(data?.grinding_energy_address),
        grinding_energy_value: getDefaultFieldValue(data?.grinding_energy_value),
        grinding_target_type_address: getDefaultFieldValue(data?.grinding_target_type_address),
        number_of_cycles_address: getDefaultFieldValue(data?.number_of_cycles_address),
        number_of_cycles_value: getDefaultFieldValue(data?.number_of_cycles_value),
        production_rate_address: getDefaultFieldValue(data?.production_rate_address),
        production_rate_value: getDefaultFieldValue(data?.production_rate_value),
        specific_grinding_energy_address: getDefaultFieldValue(data?.specific_grinding_energy_address),
        specific_grinding_energy_value: getDefaultFieldValue(data?.specific_grinding_energy_value),
        time_address_hours: getDefaultFieldValue(data?.time_address_hours),
        time_address_minutes: getDefaultFieldValue(data?.time_address_minutes),
        time_value: getDefaultFieldValue(data?.time_value)
      }}
      onSubmit={(values, formikHelpers) => submitForm(values, formikHelpers)}
      enableReinitialize
    >
      {({ setFieldValue, errors, handleBlur, handleChange, touched, values, handleSubmit, resetForm, dirty }) => {
        const getHelperText = getFormikHelperText({ errors, t });
        const getError = getFormikError({ errors, touched });
        const handleChangeNumber = getChangeNumberHandler(setFieldValue);

        return (
          <StyledNewDialog
            open={open}
            handleClose={(event, reason) => {
              if (reason && reason === 'backdropClick') return;
              resetForm();
              handleClose();
            }}
            title={t(`${labelAmend}.modal_title`)}
            subTitle={t(`${labelAmend}.modal_subtitle`)}
            maxWidth="648px"
            displayActionBar="true"
            actionBar={actionBar(handleSubmit, errors, resetForm, dirty)}
            content={
              <StyledContent>
                <FormControl sx={{ width: '100%' }}>
                  <StyledContentGridColumn>
                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('grinding_target_type_address')}
                        helperText={getHelperText('grinding_target_type_address')}
                        fullWidth
                        id="grinding_target_type_address"
                        label={t(`${labelAmendItem}.target_type_address`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="grinding_target_type_address"
                        value={getDefaultFieldValue(values.grinding_target_type_address)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4 }}>
                        <NotifyTypo.Body1 fontWeight="500">{t(`${labelAmendItem}.time`)}</NotifyTypo.Body1>
                      </Box>
                      <FormElements.TextField
                        error={getError('time_value')}
                        helperText={getHelperText('time_value')}
                        fullWidth
                        id="time_value"
                        label={t(`${labelAmendItem}.time_value`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeNumber('time_value')}
                        type="number"
                        min="0"
                        max="100000"
                        name="time_value"
                        value={getDefaultFieldValue(values.time_value)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('time_address_hours')}
                        helperText={getHelperText('time_address_hours')}
                        fullWidth
                        id="time_address_hours"
                        label={t(`${labelAmendItem}.time_address_h`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="time_address_hours"
                        value={getDefaultFieldValue(values.time_address_hours)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('time_address_minutes')}
                        helperText={getHelperText('time_address_minutes')}
                        fullWidth
                        id="time_address_minutes"
                        label={t(`${labelAmendItem}.time_address_m`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="time_address_minutes"
                        value={getDefaultFieldValue(values.time_address_minutes)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4 }}>
                        <NotifyTypo.Body1 fontWeight="500">
                          {t(`${labelAmendItem}.grinding_energy_specific`)}
                        </NotifyTypo.Body1>
                      </Box>
                      <FormElements.TextField
                        error={getError('specific_grinding_energy_value')}
                        helperText={getHelperText('specific_grinding_energy_value')}
                        fullWidth
                        id="specific_grinding_energy_value"
                        label={t(`${labelAmendItem}.grinding_energy_value`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeNumber('specific_grinding_energy_value')}
                        type="number"
                        min="0"
                        max="100000"
                        name="specific_grinding_energy_value"
                        value={getDefaultFieldValue(values.specific_grinding_energy_value)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('specific_grinding_energy_address')}
                        helperText={getHelperText('specific_grinding_energy_address')}
                        fullWidth
                        id="specific_grinding_energy_address"
                        label={t(`${labelAmendItem}.energy_address`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="specific_grinding_energy_address"
                        value={getDefaultFieldValue(values.specific_grinding_energy_address)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4 }}>
                        <NotifyTypo.Body1 fontWeight="500">{t(`${labelAmendItem}.grinding_energy`)}</NotifyTypo.Body1>
                      </Box>
                      <FormElements.TextField
                        error={getError('grinding_energy_value')}
                        helperText={getHelperText('grinding_energy_value')}
                        fullWidth
                        id="grinding_energy_value"
                        label={t(`${labelAmendItem}.energy_value`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeNumber('grinding_energy_value')}
                        type="number"
                        min="0"
                        max="100000"
                        name="grinding_energy_value"
                        value={getDefaultFieldValue(values.grinding_energy_value)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('grinding_energy_address')}
                        helperText={getHelperText('grinding_energy_address')}
                        fullWidth
                        id="grinding_energy_address"
                        label={t(`${labelAmendItem}.energy_address`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="grinding_energy_address"
                        value={getDefaultFieldValue(values.grinding_energy_address)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4 }}>
                        <NotifyTypo.Body1 fontWeight="500">{t(`${labelAmendItem}.number_cycles`)}</NotifyTypo.Body1>
                      </Box>
                      <FormElements.TextField
                        error={getError('number_of_cycles_value')}
                        helperText={getHelperText('number_of_cycles_value')}
                        fullWidth
                        id="number_of_cycles_value"
                        label={t(`${labelAmendItem}.number_cycles_value`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeNumber('number_of_cycles_value')}
                        type="number"
                        min="0"
                        max="100000"
                        name="number_of_cycles_value"
                        value={getDefaultFieldValue(values.number_of_cycles_value)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('number_of_cycles_address')}
                        helperText={getHelperText('number_of_cycles_address')}
                        fullWidth
                        id="number_of_cycles_address"
                        label={t(`${labelAmendItem}.cycles_address`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="number_of_cycles_address"
                        value={getDefaultFieldValue(values.number_of_cycles_address)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 4 }}>
                        <NotifyTypo.Body1 fontWeight="500">{t(`${labelAmendItem}.rate`)}</NotifyTypo.Body1>
                      </Box>
                      <FormElements.TextField
                        error={getError('production_rate_value')}
                        helperText={getHelperText('production_rate_value')}
                        fullWidth
                        id="production_rate_value"
                        label={t(`${labelAmendItem}.rate_value`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeNumber('production_rate_value')}
                        type="number"
                        min="0"
                        max="100000"
                        name="production_rate_value"
                        value={getDefaultFieldValue(values.production_rate_value)}
                      />
                    </StyledGridItem>

                    <StyledGridItem>
                      <FormElements.TextField
                        error={getError('production_rate_address')}
                        helperText={getHelperText('production_rate_address')}
                        fullWidth
                        id="production_rate_address"
                        label={t(`${labelAmendItem}.rate_address`)}
                        margin="none"
                        size="small"
                        onBlur={handleBlur}
                        onChange={handleChangeValue(handleChange)}
                        name="production_rate_address"
                        value={getDefaultFieldValue(values.production_rate_address)}
                      />
                    </StyledGridItem>
                  </StyledContentGridColumn>
                </FormControl>
              </StyledContent>
            }
          />
        );
      }}
    </Formik>
  );
};

IrisBatchRecordsEditTargetPLC.propTypes = {
  machineId: T.string,
  open: bool.isRequired,
  handleClose: func.isRequired,
  data: IrisBatchTargetPropTypes,
  plcType: T.shape({
    plc_type: T.string,
    autoconfig_enabled: T.bool
  }).isRequired
};

IrisBatchRecordsEditTargetPLC.defaultProps = {
  machineId: null,
  data: null
};

export default IrisBatchRecordsEditTargetPLC;
